import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────────

export type DataRuleOperator = 'equals' | 'not_equals' | 'in' | 'not_in';

export type DataRuleModule = 'data_authorization' | 'reporting' | 'automation';

export type DataRuleEntity =
  | 'contacts'
  | 'jobs'
  | 'invoices'
  | 'documents'
  | 'time_entries'
  | 'saved_reports';

export interface DataRuleCondition {
  field: string;
  operator: DataRuleOperator;
  values: string[];
}

export interface DataRule {
  id: string;
  name: string;
  description: string | null;
  field: string;
  operator: DataRuleOperator;
  values: string[];
  conditions: DataRuleCondition[] | null;
  logic: 'and' | 'or';
  isActive: boolean;
  modules: DataRuleModule[];
  entities: DataRuleEntity[];
  linkedDataRolesCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDataRuleData {
  name: string;
  description: string;
  conditions: DataRuleCondition[];
  logic: 'and' | 'or';
  modules: DataRuleModule[];
  entities: DataRuleEntity[];
}

export interface UpdateDataRuleData extends CreateDataRuleData {
  isActive: boolean;
}

export interface DataRulesFilters {
  module?: DataRuleModule;
  status?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

// ─── Query Keys ─────────────────────────────────────────────────────────────────

export const dataRulesKeys = {
  all: ['settings', 'data-rules'] as const,
  list: (filters?: DataRulesFilters) => [...dataRulesKeys.all, 'list', filters] as const,
  detail: (id: string) => [...dataRulesKeys.all, 'detail', id] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────────

/**
 * Fetch paginated and filterable list of data rules.
 */
export function useDataRules(filters?: DataRulesFilters) {
  return useQuery({
    queryKey: dataRulesKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.module) params.set('module', filters.module);
      if (filters?.status) params.set('status', filters.status);
      if (filters?.page) params.set('page', String(filters.page));
      if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));
      if (filters?.sortBy) params.set('sortBy', filters.sortBy);
      if (filters?.sortDirection) params.set('sortDirection', filters.sortDirection);

      const response = await apiClient.get<
        ApiResponse<{
          dataRules: DataRule[];
          meta: { page: number; pageSize: number; totalCount: number };
        }>
      >(`/data-rules?${params.toString()}`);

      const { dataRules, meta } = response.data.data;
      return { dataRules, total: meta.totalCount, page: meta.page, pageSize: meta.pageSize };
    },
  });
}

/**
 * Fetch a single data rule with its modules and entities.
 */
export function useDataRule(id: string) {
  return useQuery({
    queryKey: dataRulesKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<DataRule>>(`/data-rules/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

/**
 * Create a new data rule with modules and entities.
 */
export function useCreateDataRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateDataRuleData) => {
      const response = await apiClient.post<ApiResponse<DataRule>>('/data-rules', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataRulesKeys.all });
    },
  });
}

/**
 * Update an existing data rule.
 */
export function useUpdateDataRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateDataRuleData }) => {
      const response = await apiClient.put<ApiResponse<DataRule>>(`/data-rules/${id}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataRulesKeys.all });
    },
  });
}

/**
 * Delete (deactivate) a data rule.
 */
export function useDeleteDataRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/data-rules/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataRulesKeys.all });
    },
  });
}

/**
 * Replace module assignments for a data rule.
 */
export function useUpdateDataRuleModules() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, modules }: { id: string; modules: DataRuleModule[] }) => {
      const response = await apiClient.put<ApiResponse<DataRule>>(`/data-rules/${id}/modules`, {
        modules,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataRulesKeys.all });
    },
  });
}

/**
 * Replace entity assignments for a data rule.
 */
export function useUpdateDataRuleEntities() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, entities }: { id: string; entities: DataRuleEntity[] }) => {
      const response = await apiClient.put<ApiResponse<DataRule>>(`/data-rules/${id}/entities`, {
        entities,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataRulesKeys.all });
    },
  });
}
